import React, { createContext, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AlertBoxContainer from "./components/AlertBox/AlertBoxContainer";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import io from "socket.io-client";
import { useSelector } from "react-redux";
import useSocketStore from "./store/socketStore";

export const AlertContext = createContext();

export const PageContext = createContext();

const queryClient = new QueryClient();

function App() {
  const [alertData, setAlertData] = useState({ open: false, title: false });
  const { socketConnect } = useSocketStore();

  const { signed, profile } = useSelector((state) => state.auth);
  function alertBox(content) {
    setAlertData({ open: true, ...content });
  }
  useEffect(() => {
    if (signed) {
      const socketURL =
        process.env.NODE_ENV === "production"
          ? process.env.REACT_APP_BACKEND_URL
          : "http://localhost:3333";
      socketConnect(socketURL);
    }
  }, [signed]);
  const pages = [
    {
      name: "Academic",
      children: [
        {
          alias: "calendar",
          path: "/Academic/Calendar",
          title: "Calendar",
          icon: "CalendarDays",
        },
        {
          alias: "academic-passfail",
          path: "/Academic/PassFail",
          title: "Pass-Fail",
          icon: "CheckCircle2",
        },
      ],
    },
    {
      name: "Administrative",
      children: [
        {
          alias: "administrative-dashboard",
          path: "/Administrative/Dashboard",
          title: "Dashboard",
          icon: "LayoutDashboard",
        },
        {
          alias: "calendar",
          path: "/Administrative/Calendar",
          title: "Calendar",
          icon: "CalendarDays",
        },
        {
          alias: "students",
          path: "/Administrative/Students",
          title: "Students",
          icon: "GraduationCap",
        },
        {
          alias: "messages",
          path: "/Administrative/Messages",
          title: "Messages",
          icon: "MessageSquareShare",
          children: [
            { alias: "messages-templates", path: "/Administrative/Messages/Templates", title: "Templates", icon: "Files" },
            { alias: "messages-campaigns", path: "/Administrative/Messages/Campaigns", title: "Campaigns", icon: "Megaphone" },
            { alias: "messages-history", path: "/Administrative/Messages/History", title: "History", icon: "History" },
            { alias: "messages-quick-message", path: "/Administrative/Messages/QuickMessage", title: "Quick message", icon: "Zap" },
          ],
        },
        {
          alias: "hr",
          path: "/Administrative/HR",
          title: "HR",
          icon: "Building2",
          children: [
            {
              alias: "hr-department",
              path: "/Administrative/HR/Department",
              title: "Department",
              icon: "Building2",
            },
            {
              alias: "hr-jobdescription",
              path: "/Administrative/HR/JobDescription",
              title: "Job Description",
              icon: "FileText",
            },
            {
              alias: "hr-employee-movement",
              path: "/Administrative/HR/EmployeeMovement",
              title: "Employee Movement",
              icon: "GitCompare",
            },
            {
              alias: "hr-timesheet",
              path: "/Administrative/HR/Timesheet",
              title: "Timesheet",
              icon: "Clock",
            },
            {
              alias: "hr-timesheet-report",
              path: "/Administrative/HR/Report",
              title: "Timesheet Report",
              icon: "Clock",
            },
          ],
        },
        {
          alias: "staffs",
          path: "/Administrative/Staffs",
          title: "Staff",
          icon: "Users",
        },
        {
          alias: "frequency-students-administrative",
          path: "/Administrative/Frequency",
          title: "Attendance",
          icon: "Percent",
        },

        {
          alias: "surveys",
          path: "/Administrative/Surveys",
          title: "Surveys",
          icon: "UserStar",
          children: [
            {
              alias: "surveys",
              path: "/Administrative/Surveys/Surveys",
              title: "Surveys",
              icon: "UserStar",
            },
            {
              alias: "surveys-configuration",
              path: "/Administrative/Surveys/Configuration",
              title: "Configuration",
              icon: "Settings",
            },
            {
              alias: "surveys-enrollment",
              path: "/Administrative/Surveys/EnrollmentSurveys",
              title: "Enrollment Survey Answers",
              icon: "ClipboardList",
            },
          ],
        },
        {
          alias: "classrooms",
          path: "/Administrative/Classrooms",
          title: "Classrooms",
          icon: "Armchair",
        },
        {
          alias: "studentgroups",
          path: "/Administrative/StudentGroups",
          title: "Student Groups",
          icon: "GraduationCap",
        },
        {
          alias: "rotation",
          path: "/Administrative/RotationFaseOne",
          title: "Rotation 1st Step",
          icon: "Rotate3d",
        },
        {
          alias: "rotation",
          path: "/Administrative/RotationFaseTwo",
          title: "Rotation 2st Step",
          icon: "Rotate3d",
        },
        {
          alias: "administrative-reports",
          path: "/Administrative/Reports",
          title: "Reports",
          icon: "ChartLine",
        },
        {
          alias: "medical_request",
          path: "/Administrative/AbsenceExcuses",
          title: "Absence Excuses",
          icon: "FileText",
        },
      ],
    },
    {
      name: "Commercial",
      children: [
        {
          alias: "commercial-dashboard",
          path: "/Commercial/Dashboard",
          title: "Dashboard",
          icon: "LayoutDashboard",
        },
        {
          alias: "prospects",
          path: "/Commercial/Prospects",
          title: "Prospects",
          icon: "Users",
        },
        {
          alias: "ex-students",
          path: "/Commercial/ExStudents",
          title: "Ex-students",
          icon: "Users",
        },
        {
          alias: "enrollments",
          path: "/Commercial/Enrollments",
          title: "Enrollments",
          icon: "History",
        },
        {
          alias: "agents",
          path: "/Commercial/Agents",
          title: "Agents",
          icon: "CircleUserRound",
        },
        {
          alias: "partners-and-influencers",
          path: "/Commercial/PartnersAndInfluencers",
          title: "Partners & influencers",
          icon: "Handshake",
        },
        {
          alias: "campaign",
          path: "/Commercial/Campaign",
          title: "Campaign",
          icon: "Megaphone",
        },
      ],
    },
    {
      name: "Financial",
      children: [
        {
          alias: "financial-dashboard",
          path: "/Financial/Dashboard",
          title: "Dashboard",
          icon: "LayoutDashboard",
        },
        {
          alias: "financial-bank",
          path: "/Financial/Bank",
          title: "Bank",
          icon: "Landmark",
        },
        {
          alias: "financial-bank-account",
          path: "/Financial/BankAccount",
          title: "Bank Accounts",
          icon: "CreditCard",
        },
        {
          alias: "financial-payment-criteria",
          path: "/Financial/PaymentCriteria",
          title: "Payment Criteria",
          icon: "HandCoins",
        },
        {
          alias: "financial-payment-method",
          path: "/Financial/PaymentMethod",
          title: "Payment Methods",
          icon: "WalletCards",
        },
        {
          alias: "settlement_types",
          path: "/Financial/SettlementTypes",
          title: "Settlement Types",
          icon: "ReplaceAll",
        },
        {
          alias: "financial-merchants",
          path: "/Financial/Merchants",
          title: "Merchants",
          icon: "Store",
        },
        {
          alias: "financial_holds",
          path: "/Financial/FinancialHolds",
          title: "Financial Holds",
          icon: "ShieldAlert",
        },
        {
          alias: "cost-centers",
          path: "/Financial/CostCenters",
          title: "Cost Centers",
          icon: "ChartGantt",
        },
        {
          alias: "chart-of-accounts",
          path: "/Financial/ChartOfAccounts",
          title: "Chart of Accounts",
          icon: "ChartGantt",
        },
        {
          alias: "financial-issuer",
          path: "/Financial/Issuer",
          title: "Issuer",
          icon: "SatelliteDish",
        },
        {
          alias: "financial-collections",
          path: "/Financial/Collections",
          title: "Collections",
          icon: "BellRing",
          children: [
            { alias: "financial-collections-templates", path: "/Financial/Collections/Templates", title: "Templates", icon: "Files" },
            { alias: "financial-collections-campaigns", path: "/Financial/Collections/Campaigns", title: "Campaigns", icon: "Megaphone" },
            { alias: "financial-collections-history", path: "/Financial/Collections/History", title: "History", icon: "History" },
          ],
        },
        {
          alias: "global_financial",
          path: "/Financial/Global",
          title: "Global",
          icon: "Globe",
        },
        {
          alias: "financial-receivables",
          path: "/Financial/Receivables",
          title: "Receivables",
          icon: "HandCoins",
          children: [
            {
              alias: "financial-receivables",
              path: "/Financial/Receivables/Receivables",
              title: "Receivables",
              icon: "HandCoins",
            },
            {
              alias: "financial-recurrence",
              path: "/Financial/Receivables/Recurrence",
              title: "Recurrence",
              icon: "CalendarSync",
            },
            {
              alias: "financial-settlement",
              path: "/Financial/Receivables/Settlement",
              title: "Settlement",
              icon: "ReplaceAll",
            },
          ],
        },
        {
          alias: "financial-payees",
          path: "/Financial/Payees",
          title: "Payees",
          icon: "BadgeDollarSign",
          children: [
            {
              alias: "financial-payees",
              path: "/Financial/Payees/Payees",
              title: "Payees",
              icon: "BadgeDollarSign",
            },
            {
              alias: "payees-recurrence",
              path: "/Financial/Payees/Recurrence",
              title: "Recurrence",
              icon: "CalendarSync",
            },
            {
              alias: "payees-settlement",
              path: "/Financial/Payees/Settlement",
              title: "Settlement",
              icon: "ReplaceAll",
            },
          ],
        },
        {
          alias: "conciliations",
          path: "/Financial/Conciliation",
          title: "Conciliation",
          icon: "HandCoins",
          children: [
            {
              alias: "bank-conciliations",
              path: "/Financial/Conciliation/Bank",
              title: "Bank Reconciliation",
              icon: "Landmark",
            },
            {
              alias: "gravity-conciliations",
              path: "/Financial/Conciliation/Gravity",
              title: "Gravity Conciliation",
              icon: "CreditCard",
            },
            {
              alias: "other_conciliations",
              path: "/Financial/Conciliation/Other",
              title: "Other Conciliation",
              icon: "ListChecks",
            },
          ],
        },
        {
          alias: "financial-is-side",
          path: "/Financial/IS",
          title: "IS",
          icon: "ChartLine",
          children: [
            {
              alias: "is-register-side",
              path: "/Financial/IS/Register",
              title: "IS Registration",
              icon: "FileEdit",
            },
            {
              alias: "is-report-side",
              path: "/Financial/IS/Report",
              title: "IS Report",
              icon: "FileBarChart",
            },
          ],
        },
        {
          alias: "financial-cashflow-side",
          path: "/Financial/Cashflow",
          title: "Cash Flow",
          icon: "ChartLine",
          children: [
            {
              alias: "cashflow-register-side",
              path: "/Financial/Cashflow/Register",
              title: "Cash Flow Registration",
              icon: "FileEdit",
            },
            {
              alias: "cashflow-report-side",
              path: "/Financial/Cashflow/Report",
              title: "Cash Flow Report",
              icon: "FileBarChart",
            },
          ],
        },
      ],
    },
    {
      name: "DSO",
      children: [
        {
          alias: "dso-dashboard",
          path: "/DSO/Dashboard",
          title: "Dashboard",
          icon: "LayoutDashboard",
        },
        {
          alias: "i20-pendings",
          path: "/DSO/Onboard Pendings",
          title: "Onboard Pendings",
          icon: "TicketsPlane",
        },
        {
          alias: "enrollment-pre-arrivals",
          path: "/DSO/PreArrivals",
          title: "Pre-arrivals",
          icon: "PlaneTakeoff",
        },
        {
          alias: "enrollment-arrivals",
          path: "/DSO/Arrivals",
          title: "Arrivals",
          icon: "PlaneLanding",
        },
        {
          alias: "student-orientation",
          path: "/DSO/Orientation",
          title: "Orientation",
          icon: "Speech",
        },
      ],
    },
    {
      name: "Settings",
      children: [
        {
          alias: "parameters",
          path: "/Settings/Parameters",
          title: "Parameters",
          icon: "SlidersHorizontal",
        },
        {
          alias: "filials",
          path: "/Settings/Branches",
          title: "Branches",
          icon: "Building",
        },
        {
          alias: "inactivation-options",
          path: "/Settings/InactivationOptions",
          title: "Inactivation Options",
          icon: "CircleSlash",
        },
        {
          alias: "filial-types",
          path: "/Settings/BranchesTypes",
          title: "Branches Types",
          icon: "Building2",
        },
        {
          alias: "groups",
          path: "/Settings/Groups",
          title: "User Groups",
          icon: "Users",
        },
        {
          alias: "users",
          path: "/Settings/Users",
          title: "Users",
          icon: "User",
        },
        {
          alias: "languages",
          path: "/Settings/Languages",
          title: "Languages",
          icon: "Languages",
        },
        {
          alias: "program-categories",
          path: "/Settings/ProgramCategory",
          title: "Program Categories",
          icon: "BookMarked",
        },
        {
          alias: "levels",
          path: "/Settings/Levels",
          title: "Levels",
          icon: "School",
        },
        {
          alias: "language-modes",
          path: "/Settings/LanguageModes",
          title: "Language Modes",
          icon: "BookType",
        },
        {
          alias: "workloads",
          path: "/Settings/Workloads",
          title: "Workloads",
          icon: "CalendarClock",
        },
        {
          alias: "documents",
          path: "/Settings/Documents",
          title: "Documents",
          icon: "Files",
        },
        {
          alias: "processtypes",
          path: "/Settings/ProcessTypes",
          title: "Process Types",
          icon: "BookType",
        },
        {
          alias: "processsubstatuses",
          path: "/Settings/ProcessSubstatuses",
          title: "Process Substatuses",
          icon: "BookType",
        },
        {
          alias: "dataSync",
          path: "/Settings/DataSync",
          title: "Data Sync",
          icon: "FolderSync",
        },
        {
          alias: "tickets",
          path: "/Settings/Tickets",
          title: "Tickets",
          icon: "Ticket",
          children: [
            {
              alias: "tickets-list",
              path: "/Settings/Tickets/List",
              title: "Tickets",
              icon: "Ticket",
            },
            {
              alias: "ticket-levels",
              path: "/Settings/Tickets/Levels",
              title: "Level 1 Team",
              icon: "Layers",
            },
          ],
        },
      ],
    },
    {
      name: "Reports",
      children: [
        {
          alias: "report-dashboard",
          path: "/Reports/Dashboard",
          title: "Financial",
          icon: "BadgeDollarSign",
        },
        {
          alias: "report-financial",
          path: "/Reports/Financial",
          title: "Financial",
          icon: "BadgeDollarSign",
          children: [
            {
              alias: "report-financial-receivables",
              path: "/Reports/Financial/Receivables",
              title: "Receivables",
              icon: "BadgeDollarSign",
            },
            {
              alias: "report-financial-payees",
              path: "/Reports/Financial/Payees",
              title: "Payees",
              icon: "BadgeDollarSign",
            },
            {
              alias: "report-financial-delinquency",
              path: "/Reports/Financial/Delinquency",
              title: "Delinquency",
              icon: "TrendingDown",
            },
            {
              alias: "report-financial-delinquency-aging",
              path: "/Reports/Financial/DelinquencyAging",
              title: "Delinquency Aging Report",
              icon: "CalendarClock",
            },
          ],
        },
      ],
    },
    {
      name: "FillForm",
      children: [
        {
          alias: "staffs",
          path: "/fill-form/Staff",
          title: "Staff",
          icon: "Users",
        },
      ],
    },
    {
      name: "Auth",
      children: [
        {
          alias: "reset-password",
          path: "/Auth/ResetPassword",
          title: "Reset Password",
          icon: "Mail",
        },
      ],
    },
  ];
  return (
    <AlertContext.Provider value={{ alertData, setAlertData, alertBox }}>
      <QueryClientProvider client={queryClient}>
        <PageContext.Provider value={{ pages }}>
          <Outlet />
          <ToastContainer autoClose={2500} />
          <AlertBoxContainer />
        </PageContext.Provider>
      </QueryClientProvider>
    </AlertContext.Provider>
  );
}

export default App;
